import { useEffect, type RefObject } from 'react'
import { useStore } from '../core/store'
import { bottomCover } from '../core/framing'

/**
 * The bottom band of the stage the camera must not frame under, measured — and
 * the one the toasts stand clear of (`toastBottom` in `ui/Dialogs.tsx`). App
 * mounts it on the stage, so it is measured in the spatial view too, where the
 * camera ignores it and the toasts do not.
 *
 * Three pieces of chrome sit along the stage's bottom edge over the canvas: the
 * floating composer's strip (on a blank canvas, a desktop with the thread
 * closed, and a phone), the phone's node sheet, and the graph's footer (D369),
 * which runs under the field at every width and holds the transport, the
 * scrubber and the view's switches. Neither has a height anyone
 * declares — the composer grows a line when a node is selected or the text
 * wraps, and the sheet's height is the sheet's own business and is being
 * changed as this is written — so the band is READ off the DOM, the way
 * `Labels.tsx` reads every rectangle it keeps captions out from under, and a
 * change to either piece moves the camera's field with it for free.
 *
 * The desktop thread column is deliberately not a band: it is a column, and
 * App already counts it in `viewInset.left`. The phone's thread is the same
 * element in a different shape, so it is counted only below the breakpoint the
 * pane itself switches on.
 *
 * Observed rather than polled. The composer's slot is always mounted and every
 * way the band can change resizes it — the phone's sheet opening takes the
 * composer out of it, closing puts it back, the desktop thread opening moves
 * the composer into the column — so one ResizeObserver on the slot catches the
 * sheet arriving and leaving, and a second one on the sheet itself, attached
 * while it exists, catches the sheet changing height under its own steam.
 */

/** The composer's strip. The class is a name and carries no style (App). */
const COMPOSER_SLOT = '.slot-composer'
/** The thread pane, by its label: its shell is `GlassPane`, which takes no
 *  className, so the label is the only handle a selector has (Labels.tsx reads
 *  the explorations pane the same way). */
const THREAD_PANE = 'aside[aria-label="Thread"]'
/** The graph's footer (`GraphFooter` in `ui/Ledger.tsx`). */
const FOOTER = '.graphfoot'
/** The breakpoint the thread pane turns into a bottom sheet at (`Thread.tsx`). */
const NARROW = '(max-width: 767px)'

export function useFieldInset(hostRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const narrowQ = window.matchMedia(NARROW)
    let raf = 0
    let sheet: Element | null = null
    let slot: Element | null = null
    let foot: Element | null = null

    const measure = () => {
      raf = 0
      const hr = host.getBoundingClientRect()
      // A backgrounded tab reports 0×0 for everything, and a band measured
      // against nothing would clear a real inset. Keep the last good one; the
      // host's own observer fires again when the tab comes back.
      if (hr.height <= 0 || hr.width <= 0) return
      const nextSlot = document.querySelector(COMPOSER_SLOT)
      const nextSheet = narrowQ.matches ? document.querySelector(THREAD_PANE) : null
      if (nextSlot !== slot) {
        if (slot) ro.unobserve(slot)
        slot = nextSlot
        if (slot) ro.observe(slot)
      }
      if (nextSheet !== sheet) {
        if (sheet) ro.unobserve(sheet)
        sheet = nextSheet
        if (sheet) ro.observe(sheet)
      }
      const nextFoot = document.querySelector(FOOTER)
      if (nextFoot !== foot) {
        if (foot) ro.unobserve(foot)
        foot = nextFoot
        if (foot) ro.observe(foot)
      }
      const bands = [slot, sheet, foot]
        .filter((el): el is Element => el !== null)
        .map((el) => el.getBoundingClientRect())
      useStore.getState().setViewBottom(bottomCover(hr, bands))
    }
    // Coalesced to one read per frame: the slot and the sheet change in the same
    // commit when the sheet opens, and two observers firing is one measurement.
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure)
    }
    const ro = new ResizeObserver(schedule)
    ro.observe(host)
    narrowQ.addEventListener('change', schedule)
    document.addEventListener('visibilitychange', schedule)
    // …and the sheet coming and going, asked of the store as well: it stands
    // while the rail is open over a graph with anything in it. The slot only
    // resizes when the composer is in it, and a shared copy read first has
    // stood its composer down: the slot is empty either way, so the phone's sheet
    // rose over a copy and the camera went on framing the whole stage behind it.
    const unsub = useStore.subscribe((s, prev) => {
      if (
        s.railRight !== prev.railRight ||
        s.session.order.length > 0 !== prev.session.order.length > 0 ||
        // A phone's footer stands only while a replay is under way, so a
        // replay starting or ending is the footer arriving or leaving.
        s.playing !== prev.playing ||
        (s.playhead === null) !== (prev.playhead === null)
      )
        schedule()
    })
    schedule()
    return () => {
      if (raf) cancelAnimationFrame(raf)
      unsub()
      ro.disconnect()
      narrowQ.removeEventListener('change', schedule)
      document.removeEventListener('visibilitychange', schedule)
    }
  }, [hostRef])
}
